/**
 * Exposure-aware cardiovascular survivorship engine.
 *
 * Pure functions only — no React, no network. Given a survivor's cancer
 * treatment exposures plus a small set of modifiable risk factors, this
 * returns an individualized pathway: risk drivers, surveillance cadence,
 * prevention targets, primary care actions, and cardiology referral triggers.
 *
 * Every recommendation carries a source id so the UI can render provenance
 * (organization + evidence date) next to the recommendation itself. Guidance
 * across oncology, cardiology, and general medicine varies; where it does, we
 * say so rather than presenting a single number as settled.
 */

export type ExposureKey =
  | "anthracycline"
  | "chest_rt"
  | "her2"
  | "vegf"
  | "ici"
  | "proteasome"
  | "androgen_deprivation"
  | "aromatase_inhibitor"
  | "fluoropyrimidine"
  | "tki_bcr_abl"
  | "car_t"
  | "hsct";

export type RiskKey =
  | "hypertension"
  | "diabetes"
  | "dyslipidemia"
  | "smoking"
  | "obesity"
  | "sedentary"
  | "ckd"
  | "prior_cv_event"
  | "age_65"
  | "family_history";

export type Audience = "survivor" | "clinician";

export type Source = {
  id: string;
  organization: string;
  label: string;
  /** Publication or last-substantive-revision date of the cited guidance. */
  evidenceDate: string;
  url: string;
};

export type Exposure = {
  key: ExposureKey;
  label: string;
  /** Plain-language description shown to survivors. */
  survivorNote: string;
  /** Mechanistic / clinical description shown to clinicians. */
  clinicianNote: string;
  /** Baseline risk weight before dose or modifier adjustment. */
  weight: number;
  /** Optional numeric modifier prompt (e.g. cumulative anthracycline dose). */
  doseField?: { key: string; label: string; unit: string; highThreshold: number };
};

export type RiskFactor = { key: RiskKey; label: string; weight: number; survivorNote: string };

export type Recommendation = {
  id: string;
  title: string;
  detail: string;
  /** Who is expected to own this item. Feeds the shared responsibility map. */
  owner: "Oncology" | "Cardiology" | "Primary care" | "Survivor" | "Rehabilitation";
  /** Human-readable time horizon. */
  horizon: string;
  sourceIds: string[];
  /** True where cross-guideline recommendations genuinely disagree. */
  variesAcrossGuidelines?: boolean;
};

export type PathwayInput = {
  exposures: ExposureKey[];
  doses: Record<string, number>;
  risks: RiskKey[];
  yearsSinceTreatment: number;
  symptomatic: boolean;
};

export type Pathway = {
  tier: "standard" | "increased" | "high";
  tierLabel: string;
  score: number;
  drivers: string[];
  surveillance: Recommendation[];
  prevention: Recommendation[];
  primaryCare: Recommendation[];
  referralTriggers: string[];
  urgentSymptoms: string[];
  sources: Source[];
};

export const SOURCES: Record<string, Source> = {
  asco_cv: {
    id: "asco_cv",
    organization: "ASCO",
    label: "Prevention and Monitoring of Cardiac Dysfunction in Survivors of Adult Cancers",
    evidenceDate: "2017-03",
    url: "https://ascopubs.org/doi/10.1200/JCO.2016.70.5400",
  },
  esc_cardio_onc: {
    id: "esc_cardio_onc",
    organization: "European Society of Cardiology",
    label: "ESC Guidelines on Cardio-Oncology",
    evidenceDate: "2022-08",
    url: "https://academic.oup.com/eurheartj/article/43/41/4229/6673975",
  },
  nccn_survivorship: {
    id: "nccn_survivorship",
    organization: "NCCN",
    label: "NCCN Guidelines: Survivorship",
    evidenceDate: "2025",
    url: "https://www.nccn.org/guidelines/category_3",
  },
  cog_ltfu: {
    id: "cog_ltfu",
    organization: "Children's Oncology Group",
    label: "Long-Term Follow-Up Guidelines (Version 6.0)",
    evidenceDate: "2023-10",
    url: "http://www.survivorshipguidelines.org/",
  },
  aha_primary_prevention: {
    id: "aha_primary_prevention",
    organization: "AHA/ACC",
    label: "Primary Prevention of Cardiovascular Disease",
    evidenceDate: "2019-09",
    url: "https://www.ahajournals.org/doi/10.1161/CIR.0000000000000678",
  },
  acsm_exercise: {
    id: "acsm_exercise",
    organization: "ACSM",
    label: "Exercise Guidelines for Cancer Survivors",
    evidenceDate: "2019-11",
    url: "https://journals.lww.com/acsm-msse/fulltext/2019/11000/exercise_guidelines_for_cancer_survivors_.23.aspx",
  },
  asco_ici: {
    id: "asco_ici",
    organization: "ASCO",
    label: "Management of Immune-Related Adverse Events",
    evidenceDate: "2021-12",
    url: "https://ascopubs.org/doi/10.1200/JCO.21.01440",
  },
};

export const EXPOSURES: Exposure[] = [
  {
    key: "anthracycline",
    label: "Anthracycline chemotherapy (doxorubicin, epirubicin, daunorubicin)",
    survivorNote:
      "These drugs can weaken the heart muscle, sometimes years later. Higher total doses carry more risk.",
    clinicianNote:
      "Dose-dependent type I cardiotoxicity. Risk rises meaningfully above 250 mg/m² doxorubicin-equivalent, and is amplified by chest radiation, HER2 therapy, and baseline hypertension.",
    weight: 3,
    doseField: {
      key: "anthracycline_dose",
      label: "Cumulative doxorubicin-equivalent dose",
      unit: "mg/m²",
      highThreshold: 250,
    },
  },
  {
    key: "chest_rt",
    label: "Radiation involving the chest, breast, or mediastinum",
    survivorNote:
      "Chest radiation can affect heart valves, arteries, and the sac around the heart, usually many years afterward.",
    clinicianNote:
      "Accelerated coronary disease, valvular fibrosis, restrictive cardiomyopathy, conduction disease, and pericardial disease. Latency is often 10 to 20 years; risk scales with mean heart dose and younger age at exposure.",
    weight: 3,
    doseField: { key: "chest_rt_gy", label: "Approximate mean heart dose", unit: "Gy", highThreshold: 15 },
  },
  {
    key: "her2",
    label: "HER2-targeted therapy (trastuzumab, pertuzumab, T-DM1)",
    survivorNote:
      "This treatment can temporarily reduce heart pumping strength. It often improves after treatment ends, but it should still be checked.",
    clinicianNote:
      "Type II, largely reversible cardiotoxicity. Sequential or concurrent anthracycline exposure substantially increases the chance of persistent dysfunction.",
    weight: 2,
  },
  {
    key: "vegf",
    label: "VEGF inhibitors (bevacizumab, sunitinib, sorafenib, lenvatinib)",
    survivorNote: "These can raise blood pressure and, less often, affect heart function.",
    clinicianNote:
      "Hypertension is near-universal and is the dominant actionable effect; also QT prolongation, arterial thromboembolism, and reduced ejection fraction.",
    weight: 2,
  },
  {
    key: "ici",
    label: "Immune checkpoint inhibitors (pembrolizumab, nivolumab, ipilimumab, atezolizumab)",
    survivorNote:
      "Rarely these cause heart inflammation. It is uncommon but serious, so new chest symptoms deserve prompt attention.",
    clinicianNote:
      "Myocarditis is rare (<1%) but carries high case fatality, typically within the first 12 weeks. Late effects include accelerated atherosclerosis and pericardial disease.",
    weight: 2,
  },
  {
    key: "proteasome",
    label: "Proteasome inhibitors (carfilzomib, bortezomib)",
    survivorNote: "These can strain the heart and raise blood pressure, especially during treatment.",
    clinicianNote: "Heart failure, hypertension, and pulmonary hypertension; carfilzomib carries the higher signal.",
    weight: 2,
  },
  {
    key: "androgen_deprivation",
    label: "Androgen deprivation therapy",
    survivorNote:
      "Hormone therapy for prostate cancer can raise cholesterol, blood sugar, and blood pressure over time.",
    clinicianNote:
      "Metabolic syndrome, insulin resistance, dyslipidemia, and increased major adverse cardiovascular events, particularly with GnRH agonists in men with pre-existing coronary disease.",
    weight: 2,
  },
  {
    key: "aromatase_inhibitor",
    label: "Aromatase inhibitors (anastrozole, letrozole, exemestane)",
    survivorNote: "These can worsen cholesterol and bone health over years of use.",
    clinicianNote: "Modest adverse lipid shift and increased cardiovascular event rate versus tamoxifen in some trials; also bone loss.",
    weight: 1,
  },
  {
    key: "fluoropyrimidine",
    label: "Fluoropyrimidines (5-FU, capecitabine)",
    survivorNote: "These can cause chest pain from artery spasm, usually during treatment.",
    clinicianNote: "Coronary vasospasm and ischemia, typically during infusion cycles; recurrence risk on rechallenge.",
    weight: 1,
  },
  {
    key: "tki_bcr_abl",
    label: "BCR-ABL inhibitors (nilotinib, ponatinib, dasatinib)",
    survivorNote: "Some of these can affect arteries and blood pressure with long-term use.",
    clinicianNote:
      "Ponatinib and nilotinib carry arterial occlusive event risk; dasatinib is associated with pulmonary arterial hypertension and pleural effusion.",
    weight: 2,
  },
  {
    key: "car_t",
    label: "CAR T-cell therapy",
    survivorNote: "Heart effects usually happen early, around the time of treatment.",
    clinicianNote:
      "Cardiovascular events cluster with cytokine release syndrome grade; longer-term cardiac data remain limited, which itself is worth stating to patients.",
    weight: 2,
  },
  {
    key: "hsct",
    label: "Stem cell or bone marrow transplant",
    survivorNote: "Transplant survivors have higher rates of blood pressure, diabetes, and heart disease long-term.",
    clinicianNote:
      "Markedly elevated late cardiometabolic burden, compounded by total body irradiation, chronic GVHD, and prolonged corticosteroid exposure.",
    weight: 3,
  },
];

export const RISK_FACTORS: RiskFactor[] = [
  { key: "hypertension", label: "High blood pressure", weight: 2, survivorNote: "The single most modifiable amplifier of treatment-related heart risk." },
  { key: "diabetes", label: "Diabetes or prediabetes", weight: 2, survivorNote: "Raises both heart and kidney risk after treatment." },
  { key: "dyslipidemia", label: "High cholesterol", weight: 1, survivorNote: "Often worsens after hormonal therapy or transplant." },
  { key: "smoking", label: "Current or recent smoking", weight: 2, survivorNote: "Compounds radiation-related artery disease." },
  { key: "obesity", label: "Body mass index of 30 or above", weight: 1, survivorNote: "Linked to fatigue, recurrence risk, and heart strain." },
  { key: "sedentary", label: "Less than 150 minutes of activity per week", weight: 1, survivorNote: "Exercise has the strongest evidence of anything on this list." },
  { key: "ckd", label: "Reduced kidney function", weight: 2, survivorNote: "Changes which medicines and contrast studies are safe." },
  { key: "prior_cv_event", label: "Previous heart attack, stroke, or heart failure", weight: 3, survivorNote: "Moves you into the highest surveillance tier regardless of treatment." },
  { key: "age_65", label: "Age 65 or older", weight: 1, survivorNote: "Age interacts with treatment exposure rather than replacing it." },
  { key: "family_history", label: "Family history of early heart disease", weight: 1, survivorNote: "Relevant when a first-degree relative was affected before age 55 or 65." },
];

function has(input: PathwayInput, key: ExposureKey) {
  return input.exposures.includes(key);
}

export function scorePathway(input: PathwayInput): { score: number; drivers: string[] } {
  let score = 0;
  const drivers: string[] = [];

  for (const key of input.exposures) {
    const ex = EXPOSURES.find((e) => e.key === key);
    if (!ex) continue;
    let w = ex.weight;
    const dose = ex.doseField ? input.doses[ex.doseField.key] : undefined;
    if (ex.doseField && typeof dose === "number" && dose >= ex.doseField.highThreshold) {
      w += 2;
      drivers.push(`${ex.label} at ${dose} ${ex.doseField.unit}, above the ${ex.doseField.highThreshold} ${ex.doseField.unit} threshold where risk rises`);
    } else {
      drivers.push(ex.label);
    }
    score += w;
  }

  // Documented interaction effects, not simple addition.
  if (has(input, "anthracycline") && has(input, "chest_rt")) {
    score += 3;
    drivers.push("Anthracycline plus chest radiation, a combination that multiplies rather than adds risk");
  }
  if (has(input, "anthracycline") && has(input, "her2")) {
    score += 2;
    drivers.push("Sequential anthracycline and HER2 therapy, which raises the chance of persistent dysfunction");
  }

  for (const key of input.risks) {
    const rf = RISK_FACTORS.find((r) => r.key === key);
    if (!rf) continue;
    score += rf.weight;
    drivers.push(rf.label);
  }

  if (input.symptomatic) {
    score += 4;
    drivers.push("Current cardiac symptoms reported");
  }
  if (input.yearsSinceTreatment >= 10 && has(input, "chest_rt")) {
    score += 2;
    drivers.push("More than ten years since chest radiation, the window where coronary and valve disease typically appears");
  }

  return { score, drivers };
}

export function buildPathway(input: PathwayInput): Pathway {
  const { score, drivers } = scorePathway(input);
  const tier: Pathway["tier"] = input.symptomatic || score >= 10 ? "high" : score >= 5 ? "increased" : "standard";
  const tierLabel =
    tier === "high"
      ? "Higher surveillance intensity"
      : tier === "increased"
        ? "Increased surveillance intensity"
        : "Standard surveillance intensity";

  const surveillance: Recommendation[] = [];
  const prevention: Recommendation[] = [];
  const primaryCare: Recommendation[] = [];
  const referralTriggers: string[] = [];
  const sourceIds = new Set<string>();

  const add = (list: Recommendation[], rec: Recommendation) => {
    list.push(rec);
    rec.sourceIds.forEach((s) => sourceIds.add(s));
  };

  // --- Surveillance -------------------------------------------------------
  if (has(input, "anthracycline") || has(input, "her2") || has(input, "proteasome") || has(input, "car_t")) {
    add(surveillance, {
      id: "echo",
      title: "Echocardiogram with strain imaging",
      detail:
        tier === "high"
          ? "Every 1 to 2 years, and promptly with any new breathlessness, swelling, or exercise intolerance. Ask for global longitudinal strain, which detects change earlier than ejection fraction alone."
          : "Every 2 to 5 years once you are past active treatment, with strain imaging where available. Intervals differ across oncology and cardiology guidance, so confirm the plan your clinicians are following.",
      owner: "Cardiology",
      horizon: tier === "high" ? "Every 1 to 2 years" : "Every 2 to 5 years",
      sourceIds: ["asco_cv", "esc_cardio_onc", "nccn_survivorship"],
      variesAcrossGuidelines: true,
    });
  }
  if (has(input, "chest_rt")) {
    add(surveillance, {
      id: "rt_screen",
      title: "Coronary and valve screening after chest radiation",
      detail:
        "Begin about 5 to 10 years after radiation, then repeat every 5 years. Echocardiography for valve disease; discuss functional or anatomic ischemia testing, including coronary calcium scoring, given accelerated atherosclerosis in the radiation field.",
      owner: "Cardiology",
      horizon: "Start 5 to 10 years post-radiation, then every 5 years",
      sourceIds: ["esc_cardio_onc", "cog_ltfu", "nccn_survivorship"],
      variesAcrossGuidelines: true,
    });
    add(surveillance, {
      id: "carotid",
      title: "Carotid assessment if the neck was in the radiation field",
      detail: "Head and neck or supraclavicular fields raise stroke risk. Ask whether carotid ultrasound belongs in your schedule.",
      owner: "Primary care",
      horizon: "Discuss at 10 years post-radiation",
      sourceIds: ["cog_ltfu"],
    });
  }
  if (has(input, "ici")) {
    add(surveillance, {
      id: "ici_watch",
      title: "Immune checkpoint cardiac awareness",
      detail:
        "Myocarditis risk concentrates in the first 12 weeks of therapy. Beyond that window, the surveillance focus shifts to accelerated atherosclerosis and conventional risk factor control.",
      owner: "Oncology",
      horizon: "First 12 weeks, then risk-factor focused",
      sourceIds: ["asco_ici", "esc_cardio_onc"],
    });
  }
  if (has(input, "vegf") || has(input, "tki_bcr_abl") || has(input, "proteasome")) {
    add(surveillance, {
      id: "bp_home",
      title: "Home blood pressure log",
      detail:
        "Targeted therapies raise blood pressure often enough that home readings beat occasional clinic checks. Log twice weekly and bring the record to visits.",
      owner: "Survivor",
      horizon: "Twice weekly, ongoing",
      sourceIds: ["esc_cardio_onc", "aha_primary_prevention"],
    });
  }
  if (has(input, "hsct") || has(input, "androgen_deprivation") || has(input, "aromatase_inhibitor")) {
    add(surveillance, {
      id: "metabolic",
      title: "Annual metabolic panel",
      detail: "Lipids, fasting glucose or A1c, and blood pressure at least yearly. These exposures shift metabolic risk quietly and treatably.",
      owner: "Primary care",
      horizon: "Annually",
      sourceIds: ["nccn_survivorship", "aha_primary_prevention"],
    });
  }
  if (surveillance.length === 0) {
    add(surveillance, {
      id: "baseline",
      title: "Baseline cardiovascular assessment",
      detail: "Blood pressure, lipids, glucose, and a symptom review at your annual survivorship visit.",
      owner: "Primary care",
      horizon: "Annually",
      sourceIds: ["nccn_survivorship"],
    });
  }

  // --- Prevention targets -------------------------------------------------
  add(prevention, {
    id: "bp_target",
    title: input.risks.includes("hypertension") ? "Blood pressure below 130/80" : "Keep blood pressure below 130/80",
    detail:
      "Blood pressure control is the highest-yield action for anyone with anthracycline or radiation exposure. Treat it earlier and more assertively than you would without a cancer history.",
    owner: "Primary care",
    horizon: "Recheck every 3 to 6 months until at target",
    sourceIds: ["aha_primary_prevention", "esc_cardio_onc"],
  });
  add(prevention, {
    id: "exercise",
    title: "150 minutes of moderate aerobic activity weekly plus two resistance sessions",
    detail:
      "The strongest evidence of any single intervention here, and it also improves fatigue, sleep, and cognition. Supervised cardio-oncology rehabilitation is appropriate after anthracyclines or chest radiation.",
    owner: "Rehabilitation",
    horizon: "Weekly, ongoing",
    sourceIds: ["acsm_exercise", "esc_cardio_onc"],
  });
  if (input.risks.includes("dyslipidemia") || has(input, "androgen_deprivation") || has(input, "hsct") || has(input, "chest_rt")) {
    add(prevention, {
      id: "lipids",
      title: "Lipid management with treatment history counted as a risk enhancer",
      detail:
        "Standard risk calculators do not include cancer treatment. Ask that your anthracycline, radiation, or hormonal exposure be documented as a risk-enhancing factor when statin thresholds are being decided.",
      owner: "Primary care",
      horizon: "Reassess annually",
      sourceIds: ["aha_primary_prevention"],
      variesAcrossGuidelines: true,
    });
  }
  if (input.risks.includes("smoking")) {
    add(prevention, {
      id: "tobacco",
      title: "Tobacco cessation support",
      detail: "Combined pharmacotherapy plus counseling. After chest radiation this is the intervention with the largest absolute benefit.",
      owner: "Primary care",
      horizon: "Start now",
      sourceIds: ["aha_primary_prevention", "nccn_survivorship"],
    });
  }
  if (input.risks.includes("diabetes") || has(input, "hsct") || has(input, "androgen_deprivation")) {
    add(prevention, {
      id: "glycemia",
      title: "Glycemic control with a cardiovascular-protective agent where indicated",
      detail: "Where diabetes is present alongside cardiomyopathy risk, SGLT2 inhibitors carry heart failure benefit worth discussing specifically.",
      owner: "Primary care",
      horizon: "Review at next visit",
      sourceIds: ["esc_cardio_onc", "aha_primary_prevention"],
    });
  }

  // --- Primary care actions ----------------------------------------------
  add(primaryCare, {
    id: "pcp_summary",
    title: "Put the treatment summary in the chart, not just the oncology note",
    detail:
      "Cumulative anthracycline dose, radiation field and mean heart dose, and targeted therapy exposures should be visible in the primary care problem list. Most missed surveillance traces back to this gap.",
    owner: "Primary care",
    horizon: "Once, then verify annually",
    sourceIds: ["nccn_survivorship", "cog_ltfu"],
  });
  add(primaryCare, {
    id: "pcp_symptom",
    title: "Treat new breathlessness or reduced exercise tolerance as cardiac until proven otherwise",
    detail:
      "In survivors these symptoms are frequently attributed to deconditioning or anxiety. With anthracycline or radiation exposure, obtain an echocardiogram before settling on that explanation.",
    owner: "Primary care",
    horizon: "At presentation",
    sourceIds: ["asco_cv", "esc_cardio_onc"],
  });
  if (tier !== "standard") {
    add(primaryCare, {
      id: "pcp_cardio_onc",
      title: "Establish a cardio-oncology or cardiology relationship before symptoms appear",
      detail: "A named cardiologist who knows the exposure history shortens every subsequent decision.",
      owner: "Primary care",
      horizon: "Within 6 months",
      sourceIds: ["esc_cardio_onc"],
    });
  }

  // --- Referral triggers --------------------------------------------------
  referralTriggers.push("Ejection fraction drop of 10 points or more, or any fall below 50 percent");
  referralTriggers.push("Global longitudinal strain worsening by more than 15 percent from baseline");
  referralTriggers.push("New heart failure symptoms: breathlessness lying flat, ankle swelling, unexplained weight gain");
  referralTriggers.push("Blood pressure persistently above 140/90 despite two agents");
  if (has(input, "chest_rt")) referralTriggers.push("Any new murmur, or chest pain on exertion after chest radiation");
  if (has(input, "ici")) referralTriggers.push("Chest pain, palpitations, or raised troponin during or shortly after checkpoint therapy");
  if (has(input, "fluoropyrimidine")) referralTriggers.push("Chest pain during or within days of a fluoropyrimidine infusion");
  if (has(input, "tki_bcr_abl")) referralTriggers.push("Claudication, new arterial event, or unexplained breathlessness on a BCR-ABL inhibitor");

  const urgentSymptoms = [
    "Chest pain or pressure lasting more than a few minutes",
    "Sudden severe breathlessness, or breathlessness that wakes you at night",
    "Fainting, or near-fainting with exertion",
    "A fast or irregular heartbeat that does not settle",
  ];

  return {
    tier,
    tierLabel,
    score,
    drivers,
    surveillance,
    prevention,
    primaryCare,
    referralTriggers,
    urgentSymptoms,
    sources: Array.from(sourceIds)
      .map((id) => SOURCES[id])
      .filter((s): s is Source => Boolean(s)),
  };
}

export const EMPTY_INPUT: PathwayInput = {
  exposures: [],
  doses: {},
  risks: [],
  yearsSinceTreatment: 1,
  symptomatic: false,
};
